// Thin React-19-safe wrapper around vanilla Quill 2. react-quill is NOT
// compatible with React 19 (findDOMNode) — do not swap it in.
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const TOOLBAR = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "clean"],
];

// A URL lacking a scheme (bare "example.com") is normalized to https://; known
// schemes (http, https, mailto, tel) and protocol-relative "//host" are kept.
const normalizeUrl = (raw) => {
  const t = String(raw || "").trim();
  if (/^(https?:\/\/|mailto:|tel:)/i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  return `https://${t}`;
};

const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, placeholder },
  ref
) {
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const host = hostRef.current;
    const quill = new Quill(host, {
      theme: "snow",
      placeholder: placeholder || "Write your email…",
      modules: { toolbar: TOOLBAR },
    });
    quillRef.current = quill;
    if (value) quill.clipboard.dangerouslyPasteHTML(value);
    quill.on("text-change", () => {
      onChangeRef.current?.(quill.root.innerHTML);
    });
    return () => {
      quillRef.current = null;
      // Quill 2 has no destroy(); strip its generated DOM so a remount
      // (React StrictMode double-mounts in dev) doesn't stack a second
      // toolbar + editor.
      if (host) {
        host.parentElement
          ?.querySelectorAll(".ql-toolbar")
          .forEach((el) => el.remove());
        host.className = "";
        host.innerHTML = "";
      }
    };
    // mount-only: external updates handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adopt external value swaps (e.g. switching templates) without fighting typing.
  useEffect(() => {
    const q = quillRef.current;
    if (q && typeof value === "string" && value !== q.root.innerHTML && !q.hasFocus()) {
      q.clipboard.dangerouslyPasteHTML(value);
    }
  }, [value]);

  // Insert a hyperlink at the cursor. If text is selected, link the selection
  // in place; otherwise prompt for the link text too. Quill's built-in chain
  // icon is easy to miss, so this backs an obvious "🔗 Link" button.
  const insertLink = () => {
    const q = quillRef.current;
    if (!q) return;
    let range = null;
    try {
      range = q.getSelection(true);
    } catch {
      // jsdom (tests) can't compute selection bounds; fall through
    }
    if (!range) range = { index: Math.max(q.getLength() - 1, 0), length: 0 };
    const hasSelection = range.length > 0;

    let text = hasSelection ? q.getText(range.index, range.length) : "";
    if (!hasSelection) {
      const entered = window.prompt("Link text (what the reader sees):", "");
      if (entered === null) return; // cancelled
      text = entered.trim();
      if (!text) return;
    }

    const enteredUrl = window.prompt("Link URL:", "https://");
    if (enteredUrl === null || !enteredUrl.trim()) return;
    const url = normalizeUrl(enteredUrl);

    if (hasSelection) {
      q.formatText(range.index, range.length, "link", url, "user");
      q.setSelection(range.index + range.length, 0);
    } else {
      q.insertText(range.index, text, { link: url }, "user");
      q.setSelection(range.index + text.length, 0);
    }
  };

  useImperativeHandle(ref, () => ({
    insertLink,
    insertText(text) {
      const q = quillRef.current;
      if (!q) return;
      let range = null;
      try {
        range = q.getSelection(true);
      } catch {
        // jsdom (tests) can't compute selection bounds; fall through
      }
      if (!range) range = { index: Math.max(q.getLength() - 1, 0), length: 0 };
      q.insertText(range.index, text, "user");
      q.setSelection(range.index + text.length);
    },
    getHTML() {
      return quillRef.current?.root.innerHTML || "";
    },
  }));

  return (
    <div>
      <div className="rounded-md border border-slate-300 bg-white [&_.ql-toolbar]:rounded-t-md [&_.ql-container]:rounded-b-md [&_.ql-container]:min-h-[180px]">
        <div ref={hostRef} />
      </div>
      <div className="mt-1.5">
        <button
          type="button"
          onClick={insertLink}
          className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 border border-sky-200 bg-sky-50 rounded px-2.5 py-1 hover:bg-sky-100"
          title="Insert a hyperlink at the cursor (or link the selected text)"
        >
          🔗 Link
        </button>
      </div>
    </div>
  );
});

export default RichTextEditor;
